import { Controller } from 'stimulus'

/**
 * FlattenToggleController
 * Handles toggling the SPF flattening setting
 */
export default class extends Controller {
  static targets = ['toggle', 'knob', 'label']
  static values = {
    url: String
  }

  connect() {
    // Nothing to do on connect
  }

  // Toggle the flattening setting
  async toggle(event) {
    event.preventDefault()

    // Disable the toggle during request
    if (this.hasToggleTarget) {
      this.toggleTarget.disabled = true
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.success) {
        this.updateUI(!data.do_not_flatten)
        this.showToast(data.message, 'success')
      } else {
        this.showToast(data.message || 'Failed to toggle flattening', 'error')
      }
    } catch (error) {
      console.error('Error toggling flatten:', error)
      this.showToast('An error occurred. Please try again.', 'error')
    } finally {
      // Re-enable the toggle
      if (this.hasToggleTarget) {
        this.toggleTarget.disabled = false
      }
    }
  }

  // Update UI based on new state
  updateUI(enabled) {
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute('aria-checked', enabled)

      if (enabled) {
        this.toggleTarget.classList.remove('bg-gray-200')
        this.toggleTarget.classList.add('bg-indigo-600')
      } else {
        this.toggleTarget.classList.remove('bg-indigo-600')
        this.toggleTarget.classList.add('bg-gray-200')
      }
    }

    if (this.hasKnobTarget) {
      if (enabled) {
        this.knobTarget.classList.remove('translate-x-0')
        this.knobTarget.classList.add('translate-x-5')
      } else {
        this.knobTarget.classList.remove('translate-x-5')
        this.knobTarget.classList.add('translate-x-0')
      }
    }

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = enabled ? 'On' : 'Off'
    }
  }

  // Show toast notification
  showToast(message, type = 'info') {
    const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500'

    const toast = document.createElement('div')
    toast.className = `fixed bottom-4 right-4 ${bgColor} text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center`
    toast.innerHTML = `
      <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
        ${type === 'success' ? `
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        ` : `
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        `}
      </svg>
      ${message}
    `

    document.body.appendChild(toast)

    setTimeout(() => {
      toast.remove()
    }, 4000)
  }
}
